import { c as _c } from "react/compiler-runtime";
import { useFormContext, useWatch } from "react-hook-form";
import Button from "@/components/ui/button";
import { formatQuantity, formatUnit } from "@/lib/format-data";
import { DoseFormValues } from "./dose-form-schema";
import { getDoseLotCapabilities } from "./dose-lot-picker-model";
import { roundPhysicalQuantity } from "./dose-stock-planning";
import { DoseComponentInventoryEntry } from "./use-dose-component-inventory";

/** Explicit inventory commands stay separate from the staged lot selection. */
export function DoseInventoryActions(t0) {
  const $ = _c(47);
  const {
    index,
    inventory
  } = t0;
  const {
    control,
    setValue
  } = useFormContext();
  const t1 = `components.${index}`;
  let t2;
  if ($[0] !== control || $[1] !== t1) {
    t2 = {
      control,
      name: t1
    };
    $[0] = control;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const component = useWatch(t2);
  const inventoryPreview = inventory.preview;
  const allocations = component.lotAllocations;
  const multipleOptions = (inventoryPreview?.stocks.length ?? 0) > 1;
  let t3;
  if ($[3] !== component || $[4] !== inventory) {
    t3 = getDoseLotCapabilities(component, inventory);
    $[3] = component;
    $[4] = inventory;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    canCorrect: canCorrectActualUse
  } = t3;
  let t4;
  if ($[6] !== index || $[7] !== setValue) {
    t4 = next => setValue(`components.${index}.stockAdministrationQuotas`, next, {
      shouldDirty: true,
      shouldValidate: true
    });
    $[6] = index;
    $[7] = setValue;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const setQuotas = t4;
  let t5;
  if ($[9] !== allocations || $[10] !== component.id || $[11] !== inventoryPreview?.ordinaryEditMovesInventory) {
    t5 = inventoryPreview?.ordinaryEditMovesInventory === false && component.id && allocations === undefined && <p className="text-caption text-foreground-secondary">Ordinary edits keep the recorded inventory. Changing inventory quantities makes an explicit correction to actual inventory use.</p>;
    $[9] = allocations;
    $[10] = component.id;
    $[11] = inventoryPreview?.ordinaryEditMovesInventory;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== inventory.shipments) {
    t6 = inventory.shipments.some(_temp) && <p className="text-caption text-foreground-secondary">A checkout already charged inventory today. Use the checkout lot action to reuse that draw. Choosing inventory lots separately deducts inventory again. The checkout draw is not resized to match the charted dose.</p>;
    $[13] = inventory.shipments;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== component.id || $[16] !== component.quantity || $[17] !== index || $[18] !== inventory.isCheckingInventory || $[19] !== inventory.shipments || $[20] !== inventoryPreview || $[21] !== multipleOptions || $[22] !== setQuotas || $[23] !== setValue) {
    t7 = !component.id && inventoryPreview && inventory.shipments.filter(lot_0 => inventoryPreview.stocks.some(entry => entry.stockId === lot_0.stockId) && lot_0.chargedTodayQuantity != null).map(lot_1 => <Button key={`checkout-${lot_1.id}`} type="button" size="sm" variant="secondary" className="h-auto max-w-full whitespace-normal text-left" disabled={inventory.isCheckingInventory} onClick={() => {
      setValue(`components.${index}.lotAllocationBasis`, undefined, {
        shouldDirty: true
      });
      const option = inventoryPreview.stocks.find(entry_0 => entry_0.stockId === lot_1.stockId);
      if (multipleOptions) {
        setQuotas(Object.fromEntries(inventoryPreview.stocks.map(entry_1 => [entry_1.stockId, entry_1.stockId === lot_1.stockId ? component.quantity : 0])));
      }
      setValue(`components.${index}.lotAllocations`, [{
        shipmentId: lot_1.id,
        stockId: lot_1.stockId,
        quantity: roundPhysicalQuantity(component.quantity * option.perAdministration)
      }], {
        shouldDirty: true
      });
      setValue(`components.${index}.checkoutReuse`, true, {
        shouldDirty: true,
        shouldValidate: true
      });
    }}>{`Use checkout lot ${lot_1.lotNumber || "unlabeled"} · ${formatQuantity(lot_1.chargedTodayQuantity)} ${inventoryPreview.stocks.find(entry_3 => entry_3.stockId === lot_1.stockId)?.unit ? formatUnit(inventoryPreview.stocks.find(entry_2 => entry_2.stockId === lot_1.stockId).unit) : ""} already charged`}</Button>);
    $[15] = component.id;
    $[16] = component.quantity;
    $[17] = index;
    $[18] = inventory.isCheckingInventory;
    $[19] = inventory.shipments;
    $[20] = inventoryPreview;
    $[21] = multipleOptions;
    $[22] = setQuotas;
    $[23] = setValue;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== canCorrectActualUse || $[26] !== component.correctActualInventoryUse || $[27] !== index || $[28] !== setValue) {
    t8 = canCorrectActualUse && !component.correctActualInventoryUse && <Button type="button" size="sm" variant="secondary" onClick={() => setValue(`components.${index}.correctActualInventoryUse`, true, {
      shouldDirty: true
    })}>Correct actual inventory use</Button>;
    $[25] = canCorrectActualUse;
    $[26] = component.correctActualInventoryUse;
    $[27] = index;
    $[28] = setValue;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== canCorrectActualUse || $[31] !== component.correctActualInventoryUse || $[32] !== index || $[33] !== setValue) {
    t9 = canCorrectActualUse && component.correctActualInventoryUse && <div className="space-y-1"><p className="text-caption text-foreground-secondary">Correct the actual amount used from this sale's original lots. This does not change the sale or its price. Any extra yield is shown as a warning and recorded when you save.</p><Button type="button" size="sm" variant="ghost" onClick={() => {
        setValue(`components.${index}.lotAllocationBasis`, undefined, {
          shouldDirty: true
        });
        setValue(`components.${index}.lotAllocations`, undefined, {
          shouldDirty: true
        });
        setValue(`components.${index}.stockAdministrationQuotas`, undefined, {
          shouldDirty: true
        });
        setValue(`components.${index}.correctActualInventoryUse`, false, {
          shouldDirty: true
        });
      }}>Keep recorded inventory</Button></div>;
    $[30] = canCorrectActualUse;
    $[31] = component.correctActualInventoryUse;
    $[32] = index;
    $[33] = setValue;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== allocations?.length || $[36] !== canCorrectActualUse || $[37] !== index || $[38] !== setValue) {
    t10 = canCorrectActualUse && !!allocations?.length && <Button type="button" size="sm" variant="ghost" onClick={() => {
      setValue(`components.${index}.lotAllocationBasis`, undefined, {
        shouldDirty: true
      });
      setValue(`components.${index}.lotAllocations`, canCorrectActualUse ? undefined : [], {
        shouldDirty: true,
        shouldValidate: true
      });
      if (canCorrectActualUse) {
        setValue(`components.${index}.stockAdministrationQuotas`, undefined, {
          shouldDirty: true
        });
      }
      setValue(`components.${index}.checkoutReuse`, false, {
        shouldDirty: true
      });
      setValue(`components.${index}.correctActualInventoryUse`, false, {
        shouldDirty: true
      });
    }}>Discard inventory correction</Button>;
    $[35] = allocations?.length;
    $[36] = canCorrectActualUse;
    $[37] = index;
    $[38] = setValue;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== t10 || $[41] !== t5 || $[42] !== t6 || $[43] !== t7 || $[44] !== t8 || $[45] !== t9) {
    t11 = <>{t5}{t6}{t7}{t8}{t9}{t10}</>;
    $[40] = t10;
    $[41] = t5;
    $[42] = t6;
    $[43] = t7;
    $[44] = t8;
    $[45] = t9;
    $[46] = t11;
  } else {
    t11 = $[46];
  }
  return t11;
}
function _temp(lot) {
  return (lot.chargedTodayQuantity ?? 0) > 0;
}